<template>
  <div class="nwmap">
    <!-- 세계지도: 선택 국가 강조 + 핀 -->
    <section class="nwmap-panel nwmap-world">
      <div class="nwmap-head"><span class="nwmap-pip"></span> Global coverage <span class="nwmap-tag home">{{ current.label }}</span></div>
      <div class="nwmap-canvas world">
        <svg ref="worldSvg" :viewBox="`0 0 ${WW} ${WH}`" preserveAspectRatio="xMidYMid meet"
             role="img" :aria-label="`세계지도 — ${current.label} 선택됨. 국가 선택은 상단 국가 목록을 이용하세요.`"
             :style="{ cursor: dragging ? 'grabbing' : 'grab' }"
             @pointerdown="onDown" @pointermove="onMove" @pointerup="onUp" @pointerleave="onUp"
        >
          <g :transform="groupTransform">
            <path v-for="(p, i) in worldPaths" :key="'w' + i" :d="p.d" :data-seq="p.seq"
                  :class="['nwmap-geo', { home: p.home, preview: p.preview, codable: p.seq }]"
                  @mouseover="onGeoOver(p)" @mouseout="onGeoOut()" @click="onGeoClick(p)"
            />
            <!-- 핀/라벨은 장식 — 마우스 이벤트를 받으면 아래 path의 mouseout이 발화되어 호버 깜빡임 발생 -->
            <g v-if="worldPin" :transform="`translate(${worldPin[0]},${worldPin[1]})`" pointer-events="none">
              <circle class="nwmap-ring" r="9" />
              <line class="nwmap-stem" x1="0" y1="0" x2="0" y2="-15" />
              <g transform="translate(0,-15)">
                <rect class="nwmap-pill" :x="-4" y="-14" :width="pillW" height="16" rx="5" />
                <!-- 상자 중심(x=폭/2-4, y=-6)에 middle 앵커 + dy로 수직 보정 → 항상 정중앙 -->
                <text class="nwmap-pill-txt" :x="pillW / 2 - 4" y="-6" dy=".35em" text-anchor="middle">{{ current.label }}</text>
              </g>
              <circle class="nwmap-dot" r="3.4" />
            </g>
            <g v-if="hoverPin" :transform="`translate(${hoverPin.x},${hoverPin.y})`" pointer-events="none">
              <circle class="nwmap-hdot" r="3.2" />
              <g transform="translate(0,-13)">
                <rect class="nwmap-hpill" x="-4" y="-13" :width="hoverPin.w" height="16" rx="5" />
                <text class="nwmap-hpill-txt" :x="hoverPin.w / 2 - 4" y="-5" dy=".35em" text-anchor="middle">{{ hoverPin.label }}</text>
              </g>
            </g>
          </g>
        </svg>
      </div>
    </section>

    <!-- 확대 인셋: 선택 국가 + 도시 마커 -->
    <!-- 인셋은 지도 내용의 시각적 확대 복제 — 스크린리더에는 중복이라 숨김 -->
    <aside ref="inset" class="nwmap-panel nwmap-inset" :class="{ hidden: insetHidden, big: insetBig }" :key="seq" aria-hidden="true">
      <div class="nwmap-head"><span class="nwmap-pip"></span> {{ current.label }} focus <span class="nwmap-tag zoom">ZOOM</span></div>
      <div class="nwmap-canvas inset">
        <svg :viewBox="`0 0 ${IW} ${IH}`" preserveAspectRatio="xMidYMid meet">
          <path v-for="(p, i) in insetPaths" :key="'i' + i" :d="p.d" :class="['nwmap-geo2', { home: p.home }]" />
          <!-- 도시 마커 클릭 → 부모에서 상세로 스크롤 (DB 마커는 ofcCd 키, CFG 폴백은 이름 매칭) -->
          <!-- insetMarkersDraw: 대표 마커를 마지막에 그려 좌표가 겹쳐도 다른 마커에 가려지지 않게 -->
          <!-- 확대 인셋(big)은 물리 배율이 커서 마커 그룹을 축소해 일반 인셋(예: MALAYSIA)과 체감 크기를 맞춤 -->
          <g v-for="(m, i) in insetMarkersDraw" :key="'m' + i" :transform="`translate(${m.x},${m.y}) scale(${mkScale})`" class="nwmap-mk-g" @click="$emit('city', { name: m.name, ofcCd: m.ofcCd })">
            <!-- 대표도시 펄스는 전 국가 공통 — 국가색 강조가 있는 나라는 작게(r15), geo:null 도시국가는 크게(r26)+글로우 -->
            <g v-if="m.primary">
              <circle v-if="!current.geo" class="nwmap-mk-glow" r="26" />
              <circle class="nwmap-mk-pulse" :r="current.geo ? 15 : 26" />
            </g>
            <circle v-if="m.primary" class="nwmap-mk-ring" r="13" />
            <rect :class="m.primary ? 'nwmap-mk' : 'nwmap-mk2'" x="-4.2" y="-4.2" width="8.4" height="8.4" rx="1.6" />
            <text class="nwmap-mk-label" :style="{ fontSize: m.fs + 'px' }" :text-anchor="m.anchor" :dx="m.dx" :dy="m.dy">{{ m.disp }}</text>
          </g>
        </svg>
      </div>
    </aside>
  </div>
</template>

<script>
/* eslint-disable quote-props */
import { geoMercator, geoPath, geoBounds } from 'd3-geo'
import world from '@/assets/network/world-countries.json'

// seq → { 메뉴라벨, geojson 국가명(없으면 null=도시폴백), 도시마커[lng,lat,name,primary?] }
const CFG = {
  '01': { label: 'KOREA', geo: 'South Korea', cities: [[126.98, 37.57, 'Seoul', true], [126.71, 37.46, 'Incheon'], [129.31, 35.54, 'Ulsan'], [129.08, 35.18, 'Busan']] },
  '02': { label: 'JAPAN', geo: 'Japan', cities: [[139.69, 35.69, 'Tokyo', true]] },
  '03': { label: 'CHINA', geo: 'China', cities: [[121.47, 31.23, 'Shanghai', true]] },
  '04': { label: 'HONGKONG', geo: null, cities: [[114.17, 22.32, 'Hong Kong', true]] },
  '05': { label: 'PHILIPPINES', geo: 'Philippines', cities: [[120.98, 14.60, 'Manila', true]] },
  '06': { label: 'SINGAPORE', geo: null, cities: [[103.82, 1.35, 'Singapore', true]] },
  '07': { label: 'MALAYSIA', geo: 'Malaysia', cities: [[101.69, 3.14, 'Kuala Lumpur', true]] },
  '08': { label: 'INDONESIA', geo: 'Indonesia', cities: [[106.85, -6.21, 'Jakarta', true]] },
  '09': { label: 'THAILAND', geo: 'Thailand', cities: [[100.50, 13.75, 'Bangkok', true]] },
  '10': { label: 'VIETNAM', geo: 'Vietnam', cities: [[106.63, 10.82, 'Ho Chi Minh', true]] },
  '11': { label: 'BANGLADESH', geo: 'Bangladesh', cities: [[90.41, 23.81, 'Dhaka', true]] },
  '12': { label: 'INDIA', geo: 'India', cities: [[72.88, 19.08, 'Mumbai', true]] },
  '13': { label: 'CAMBODIA', geo: 'Cambodia', cities: [[104.92, 11.56, 'Phnom Penh', true]] },
  '15': { label: 'SRILANKA', geo: 'Sri Lanka', cities: [[79.86, 6.93, 'Colombo', true]] },
  '16': { label: 'U.A.E', geo: 'United Arab Emirates', cities: [[55.27, 25.20, 'Dubai', true]] },
  '17': { label: 'MYANMAR', geo: 'Myanmar', cities: [[96.20, 16.87, 'Yangon', true]] },
  '18': { label: 'PAKISTAN', geo: 'Pakistan', cities: [[67.01, 24.86, 'Karachi', true]] },
  '19': { label: 'SAUDI ARABIA', geo: 'Saudi Arabia', cities: [[39.20, 21.49, 'Jeddah', true]] },
  '20': { label: 'OMAN', geo: 'Oman', cities: [[58.41, 23.59, 'Muscat', true]] },
  '21': { label: 'KENYA', geo: 'Kenya', cities: [[39.67, -4.04, 'Mombasa', true]] },
  '22': { label: 'IRAQ', geo: 'Iraq', cities: [[44.36, 33.31, 'Baghdad', true]] },
  '23': { label: 'KUWAIT', geo: 'Kuwait', cities: [[47.98, 29.38, 'Kuwait City', true]] },
  '24': { label: 'TANZANIA', geo: 'Tanzania', cities: [[39.28, -6.79, 'Dar es Salaam', true]] },
  '25': { label: 'EGYPT', geo: 'Egypt', cities: [[31.24, 30.04, 'Cairo', true]] },
  '26': { label: 'JORDAN', geo: 'Jordan', cities: [[35.93, 31.95, 'Amman', true]] },
  '27': { label: 'MEXICO', geo: 'Mexico', cities: [[-99.13, 19.43, 'Mexico City', true]] },
  '28': { label: 'USA', geo: 'United States of America', box: [[-125, 49], [-66, 24]], cities: [[-118.24, 34.05, 'Los Angeles', true]] },
  '29': { label: 'BAHRAIN', geo: null, cities: [[50.58, 26.22, 'Manama', true]] },
  '30': { label: 'TAIWAN', geo: 'Taiwan', cities: [[120.30, 22.62, 'Kaohsiung', true]] }
}

// 세계지도는 선택국 경도를 중심으로 수동 스케일(아래 worldProj) — 패널 폭을 꽉 채움

export default {
  name: 'NetworkMap',
  props: {
    seq: { type: String, default: '01' },
    hoverSeq: { type: String, default: '' },
    hoverSeqs: { type: Array, default: () => [] }, // 권역 호버: 여러 국가 동시 하이라이트
    cities: { type: Array, default: () => [] } // DB(WB320I MAP_MARK_YN='Y') 도시 마커 [lng, lat, 이름, 대표] — 비면 CFG 폴백
  },
  data () {
    // WH를 줄이면 선택국 위도 기준 상하가 균등 크롭됨(러시아 북부 등 제외) — 지도 높이 축소로 하단 상세표 노출
    return { WW: 920, WH: 240, IW: 360, IH: 360, features: Object.freeze(world.features), viewLng: null, viewLat: null, panX: 0, panY: 0, panK: 1, dragging: false, insetHidden: false }
  },
  computed: {
    current () { return CFG[this.seq] || CFG['01'] },
    // 마커 밀집국(CHINA 03 / INDONESIA 08 / INDIA 12)은 인셋(상세 지도) 자체를 크게 표시
    insetBig () { return ['03', '08', '12'].indexOf(this.seq) >= 0 },
    // 확대 인셋의 마커 그룹 축소 배율 — 템플릿(scale)과 겹침 판정 로직이 반드시 같은 값을 공유
    mkScale () { return this.insetBig ? 0.6 : 1 },
    // 도시 마커 소스: DB 좌표(cities prop) 우선, 없으면 내장 CFG
    effCities () { return (this.cities && this.cities.length) ? this.cities : this.current.cities },
    primaryCity () { const cs = this.effCities; return cs.find(c => c[3]) || cs[0] },
    pillW () { return 12 + this.current.label.length * 5.6 }, // 8.5px 글자 기준 폭
    selFeature () { const g = this.current.geo; return g ? this.features.find(f => f.properties.name === g) : null },
    center () { return (this.viewLng === null) ? this.primaryCity : [this.viewLng, this.viewLat] },
    groupTransform () { return `translate(${this.panX},${this.panY}) scale(${this.panK})` },
    nameToSeq () { const m = {}; for (const k in CFG) { if (CFG[k].geo) m[CFG[k].geo] = k } return m },
    hoverPin () {
      if (!this.hoverSeq || this.hoverSeq === this.seq || !CFG[this.hoverSeq]) return null
      const cs = CFG[this.hoverSeq].cities; const c = cs.find(x => x[3]) || cs[0]
      const xy = this.worldProj([c[0], c[1]]); if (!xy) return null
      const label = CFG[this.hoverSeq].label
      return { x: xy[0], y: xy[1], label, w: 12 + label.length * 5.6 }
    },

    worldProj () {
      // 선택국을 화면 정중앙(좌우=경도 회전, 상하=위도 보정)에 두고, 전 세계가 패널 폭을 꽉 채우도록 수동 스케일
      const c = this.center
      const k = ((this.WW - 12) / (2 * Math.PI)) * 1.25 // 전폭 레이아웃이라 줌은 낮춰 전 세계가 크게 다 보이도록
      const myc = Math.log(Math.tan(Math.PI / 4 + (c[1] * Math.PI) / 360)) // 선택국 위도의 머케이터 y
      // 선택국을 가로 40% 지점(좌측)에 두어 우측 오버레이에 안 가리게
      return geoMercator().rotate([-c[0], 0]).scale(k).translate([this.WW * 0.4, this.WH / 2 + k * myc])
    },
    worldPathGen () { return geoPath(this.worldProj) },
    worldPaths () {
      const g = this.current.geo
      // 단일(국가 칩/지도 호버) + 다중(권역 호버) 하이라이트 대상 geo명 수집
      const hvNames = []
      if (this.hoverSeq && CFG[this.hoverSeq] && CFG[this.hoverSeq].geo) hvNames.push(CFG[this.hoverSeq].geo)
      const seqsArr = this.hoverSeqs || []
      seqsArr.forEach(s => { if (CFG[s] && CFG[s].geo) hvNames.push(CFG[s].geo) })
      const n2s = this.nameToSeq
      return this.features
        .filter(f => f.properties.name !== 'Antarctica')
        .map(f => { const nm = f.properties.name; return { d: this.worldPathGen(f), home: !!g && nm === g, preview: hvNames.indexOf(nm) >= 0 && nm !== g, seq: n2s[nm] || null } })
    },
    worldPin () { const c = this.primaryCity; const xy = this.worldProj([c[0], c[1]]); return xy || null },

    insetProj () {
      // 확대 인셋: 좌우 여백 40(라벨 잘림 방지), 위 여백은 줄이고 아래를 늘려 지도를 위쪽으로 끌어올림
      const pad = this.insetBig ? 40 : 26
      const padT = this.insetBig ? 0 : 26
      const padB = this.insetBig ? 84 : 26
      let fitObj
      const b = this.current.box
      if (b) {
        fitObj = { type: 'Polygon', coordinates: [[[b[0][0], b[0][1]], [b[1][0], b[0][1]], [b[1][0], b[1][1]], [b[0][0], b[1][1]], [b[0][0], b[0][1]]]] }
      } else if (this.selFeature) {
        fitObj = this.selFeature
      } else {
        const c = this.primaryCity; const d = 2.2
        fitObj = { type: 'Polygon', coordinates: [[[c[0] - d, c[1] + d], [c[0] + d, c[1] + d], [c[0] + d, c[1] - d], [c[0] - d, c[1] - d], [c[0] - d, c[1] + d]]] }
      }
      return geoMercator().fitExtent([[pad, padT], [this.IW - pad, this.IH - padB]], fitObj)
    },
    insetPathGen () { return geoPath(this.insetProj) },
    insetPaths () {
      const proj = this.insetProj
      const sel = this.selFeature
      const tl = proj.invert([0, 0]); const br = proj.invert([this.IW, this.IH])
      if (!tl || !br) return sel ? [{ d: this.insetPathGen(sel), home: true }] : []
      const minLng = Math.min(tl[0], br[0]) - 1; const maxLng = Math.max(tl[0], br[0]) + 1
      const minLat = Math.min(tl[1], br[1]) - 1; const maxLat = Math.max(tl[1], br[1]) + 1
      // 주변국(선택국 제외): 경계상자가 보이는 영역과 겹치는 것. 경도180 넘는 국가는 제외(왜곡 방지)
      const list = this.features.filter(f => {
        if (f === sel) return false
        let bb; try { bb = geoBounds(f) } catch (e) { return false }
        if (!bb || !isFinite(bb[0][0]) || bb[0][0] > bb[1][0]) return false
        return bb[0][0] <= maxLng && bb[1][0] >= minLng && bb[0][1] <= maxLat && bb[1][1] >= minLat
      }).map(f => ({ d: this.insetPathGen(f), home: false }))
      // 선택국은 항상 마지막에(위에) 그려 강조
      if (sel) list.push({ d: this.insetPathGen(sel), home: true })
      return list
    },
    // 렌더 순서: 대표 마커를 맨 뒤(맨 위)로 — 좌표 중복 시 대표가 일반 마커에 덮이는 것 방지
    insetMarkersDraw () {
      return this.insetMarkers.slice().sort((a, b) => (a.primary ? 1 : 0) - (b.primary ? 1 : 0))
    },
    insetMarkers () {
      const proj = this.insetProj
      const raw = this.effCities.map(c => {
        const xy = proj([c[0], c[1]])
        // disp: 표시용 축약명 — 괄호 보조표기 제거 ("SHANTOU (IN GUANGDONG)" → "SHANTOU"). 초장문 라벨이 클러스터를 덮는 것 방지
        return { x: xy[0], y: xy[1], name: c[2], disp: String(c[2]).replace(/\s*\(.*?\)\s*$/, ''), primary: !!c[3], ofcCd: c[4] || '' }
      })
      // 라벨 배치: 마커 "바로 옆" 4방향(선호측→반대측→위→아래) 우선, 그래도 안 되면 반 칸 위/아래로만 살짝 이동
      // 폰트: 확대 인셋(big)은 15 고정(그룹 mkScale 축소로 체감 크기는 일반 인셋과 유사), 그 외엔 밀집 시 축소
      const fs = this.insetBig ? 15 : (raw.length > 3 ? 12.5 : 14)
      // s: 렌더 시 마커 그룹에 적용되는 축소 배율 — 겹침 판정 상자도 같은 배율로 계산해야 실물과 일치
      const s = this.mkScale
      const lw = n => 10 + String(n).length * fs * 0.62 // 라벨 폭 추정 (viewBox 단위, 축소 전)
      const placed = []
      // 마커 점/링 자체도 장애물로 등록 — 라벨이 다른 마커 위에 얹히지 않게 (자기 마커는 제외)
      const mkBoxes = raw.map(m => {
        const r = (m.primary ? 14 : 7) * s
        return { x0: m.x - r, x1: m.x + r, y0: m.y - r, y1: m.y + r }
      })
      const ov = (a, b) => a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0
      const hits = (b, selfIdx) => placed.some(p => ov(b, p)) || mkBoxes.some((p, pi) => pi !== selfIdx && ov(b, p))
      const inBounds = b => b.x0 >= 4 && b.x1 <= this.IW - 4
      // 모드: r=오른쪽, l=왼쪽, t=위(중앙정렬), b=아래(중앙정렬)
      // 상자 좌표는 마커 기준 오프셋에 s를 곱해 "실제 렌더 위치"로 계산
      const box = (m, mode, dy) => {
        const w = lw(m.disp) * s
        const x0 = mode === 'r' ? m.x + 9 * s : mode === 'l' ? m.x - 9 * s - w : m.x - w / 2
        return { x0, x1: x0 + w, y0: m.y + (dy - fs * 0.8) * s, y1: m.y + (dy + fs * 0.4) * s }
      }
      const step = fs + 3
      const half = Math.round(step / 2)
      return raw.map((m, mi) => {
        const pref = m.x > this.IW * 0.62 ? ['l', 'r'] : ['r', 'l'] // 오른쪽 가장자리 마커는 왼쪽 선호 (잘림 방지)
        const up = -(m.primary ? 19 : 13)
        const dn = m.primary ? 24 : 18
        const cands = [
          [pref[0], -4], [pref[1], -4], ['t', up], ['b', dn],
          [pref[0], -4 - half], [pref[1], -4 - half], [pref[0], -4 + half], [pref[1], -4 + half],
          [pref[0], -4 - step], [pref[1], -4 - step], [pref[0], -4 + step], [pref[1], -4 + step],
          ['t', up - half], ['b', dn + half]
        ]
        let pick = null
        for (let ci = 0; ci < cands.length; ci++) {
          const bx = box(m, cands[ci][0], cands[ci][1])
          if (inBounds(bx) && !hits(bx, mi)) { pick = { mode: cands[ci][0], dy: cands[ci][1] }; break }
        }
        if (!pick) pick = { mode: pref[0], dy: -4 } // 모든 슬롯 실패 시 선호측 기본 위치
        placed.push(box(m, pick.mode, pick.dy))
        const anchor = pick.mode === 'r' ? 'start' : pick.mode === 'l' ? 'end' : 'middle'
        const dx = pick.mode === 'r' ? 9 : pick.mode === 'l' ? -9 : 0
        return Object.assign({}, m, { anchor: anchor, dx: dx, dy: pick.dy, fs: fs })
      })
    }
  },
  watch: {
    seq () {
      this.panX = 0; this.panY = 0; this.panK = 1
      const pc = this.primaryCity
      this.flyTo(pc[0], pc[1])
    },
    // 강조된 국가가 인셋(미니지도)에 가려지면 인셋을 잠깐 숨김
    hoverSeq (s) {
      this.$nextTick(() => {
        const inset = this.$refs.inset; const svg = this.$refs.worldSvg
        if (!inset || !svg || !s || s === this.seq) { this.insetHidden = false; return }
        const el = svg.querySelector('[data-seq="' + s + '"]')
        if (!el) { this.insetHidden = false; return }
        const a = el.getBoundingClientRect(); const b = inset.getBoundingClientRect()
        this.insetHidden = !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom)
      })
    }
  },
  mounted () {
    const pc = this.primaryCity
    this.viewLng = pc[0]; this.viewLat = pc[1]
  },
  beforeDestroy () {
    if (this._raf) cancelAnimationFrame(this._raf)
  },
  methods: {
    // ③ 선택국으로 부드럽게 회전이동(fly-to): 투영 중심(경/위도)을 트위닝
    flyTo (toLng, toLat) {
      if (this._raf) cancelAnimationFrame(this._raf)
      // 모션 최소화 설정 사용자는 트위닝 없이 즉시 이동
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.viewLng = toLng; this.viewLat = toLat
        return
      }
      const fromLng = this.center[0]; const fromLat = this.center[1]
      const dLng = (((toLng - fromLng) % 360) + 540) % 360 - 180 // 최단 경로
      const dLat = toLat - fromLat; const dur = 700; let t0 = null
      const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
      const step = ts => {
        if (t0 === null) t0 = ts
        const t = Math.min(1, (ts - t0) / dur); const e = ease(t)
        this.viewLng = fromLng + dLng * e; this.viewLat = fromLat + dLat * e
        if (t < 1) { this._raf = requestAnimationFrame(step) } else { this._raf = null }
      }
      this._raf = requestAnimationFrame(step)
    },
    // 권역 클릭: 소속국 대표도시 평균 지점으로 이동 (부모에서 $refs로 호출)
    flyToRegion (seqs) {
      let sx = 0; let sy = 0; let n = 0
      ;(seqs || []).forEach(s => {
        const cfg = CFG[s]
        if (!cfg) return
        const c = cfg.cities.find(x => x[3]) || cfg.cities[0]
        sx += c[0]; sy += c[1]; n++
      })
      if (!n) return
      this.panX = 0; this.panY = 0; this.panK = 1
      this.flyTo(sx / n, sy / n)
    },
    // ① 드래그 팬 + 휠 줌 (재투영 없이 그룹 transform)
    onDown (e) {
      this.dragging = true; this._moved = false; this._down = [e.clientX, e.clientY]; this._last = [e.clientX, e.clientY]
      if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null } // 비행 중 드래그 시작 시 fly-to 중단
      // 주의: 여기서 바로 setPointerCapture 하면 click이 path가 아닌 svg로 재타깃되어 국가 클릭이 죽음 → 드래그 확정 시점(onMove)에 캡처
    },
    onMove (e) {
      if (!this.dragging || !this._last) return
      if (!this._moved && Math.abs(e.clientX - this._down[0]) + Math.abs(e.clientY - this._down[1]) > 4) {
        this._moved = true
        try { e.currentTarget.setPointerCapture(e.pointerId) } catch (_) { /* 캡처 미지원 무시 */ }
      }
      const rect = e.currentTarget.getBoundingClientRect()
      const dx = (e.clientX - this._last[0]) * (this.WW / rect.width)
      const dy = (e.clientY - this._last[1]) * (this.WH / rect.height)
      // 가로: translate 팬 대신 투영 중심 경도를 회전 → 좌우로 끝없이 이어지는 지도 (랩어라운드)
      const k = ((this.WW - 12) / (2 * Math.PI)) * 1.25 // worldProj와 동일 스케일
      this.viewLat = this.center[1] // viewLng/viewLat 미초기화 대비
      this.viewLng = (((this.center[0] - (dx / (k * this.panK)) * (180 / Math.PI)) + 540) % 360) - 180
      this.panY += dy
      this._last = [e.clientX, e.clientY]; this.clampPan()
    },
    onUp () { this.dragging = false; this._last = null },
    // 지도 국가폴리곤(코드 보유국)만: 마우스오버=하이라이트, 클릭=해당국 이동(부모에 emit)
    onGeoOver (p) { if (p.seq && p.seq !== this.hoverSeq) this.$emit('hover', p.seq) },
    onGeoOut () { this.$emit('hover', '') },
    onGeoClick (p) { if (p.seq && !this._moved) { this.$emit('hover', ''); this.$emit('select', p.seq) } },
    clampPan () {
      const mx = this.WW * (this.panK + 0.6); const my = this.WH * (this.panK * 0.5 + 0.3)
      this.panX = Math.max(-mx, Math.min(mx, this.panX))
      this.panY = Math.max(-my, Math.min(my, this.panY))
    }
  }
}
</script>

<style scoped>
/* [세계지도 전폭 + 우측 국가 오버레이(PiP)] 선택국은 지도 좌측에 두어 오버레이에 안 가리게 */
/* user-select:none — 더블클릭 시 라벨 텍스트가 선택되면 이후 드래그가 선택영역 확장으로 동작해 지도 팬이 막힘 */
.nwmap { position: relative; width: 100%; box-sizing: border-box; user-select: none; -webkit-user-select: none; }
.nwmap-panel { background: #fff; border: 1px solid #d4def0; border-radius: 14px; overflow: hidden; box-shadow: 0 14px 32px -26px rgba(11, 31, 58, .4); }
.nwmap-world { width: 100%; display: flex; flex-direction: column; }
/* 세계지도의 선택국 위치(가로 40%·세로 중앙)에서 확대되어 우측 오버레이 자리로 날아오는 효과 */
@keyframes nwmapZoomFrom {
  0% { opacity: 0; transform: translate(-520px, 0) scale(.06); }
  12% { opacity: 1; }
  100% { opacity: 1; transform: translate(0, 0) scale(1); }
}
/* 세로: 패널 위아래 20px만 남기고 꽉 채움. 가로: 세로에 비례(aspect-ratio) — 헤더+정사각 지도 비율 근사값 */
.nwmap-inset { position: absolute; top: 20px; bottom: 20px; right: 18px; width: auto; aspect-ratio: 22 / 25; display: flex; flex-direction: column; background: #0a1f3d; border-color: #0c2247; z-index: 5; box-shadow: 0 22px 46px -16px rgba(8, 20, 40, .72); transform-origin: center center; animation: nwmapZoomFrom .85s cubic-bezier(.65, 0, .35, 1) both; transition: opacity .25s ease; }
/* 마커 밀집국(CHINA/INDIA/INDONESIA): 인셋 확대 — 위아래 대칭으로 경계를 넘어 패널 세로 중앙 유지 */
.nwmap-inset.big { top: -30px; bottom: -5px; right: -5px; aspect-ratio: 28 / 25; }
/* 인셋 svg는 남는 높이를 가득 채움 (meet라 지도는 중앙 유지, 여백은 다크 배경과 자연스럽게 융화) */
.nwmap-canvas.inset svg { width: 100%; height: 100%; }
.nwmap-inset.hidden { opacity: 0 !important; pointer-events: none; }
.nwmap-head { display: flex; align-items: center; gap: 8px; padding: 11px 14px; font-size: 11px; letter-spacing: .12em; text-transform: uppercase; font-weight: 600; }
.nwmap-world .nwmap-head { color: #5a6b82; border-bottom: 1px solid #e6edf7; }
.nwmap-inset .nwmap-head { color: #cfe2fb; border-bottom: 1px solid rgba(120, 170, 225, .18); }
.nwmap-pip { width: 8px; height: 8px; border-radius: 2px; background: #ff6a1a; box-shadow: 0 0 0 3px rgba(255, 106, 26, .18); }
.nwmap-tag { margin-left: auto; font-size: 10px; letter-spacing: .1em; padding: 3px 8px; border-radius: 999px; }
.nwmap-tag.home { color: #e85d12; background: #ffe2d2; }
.nwmap-hpill { fill: #2c4f8f; }
.nwmap-hpill-txt { fill: #fff; font-size: 8.5px; font-weight: 500; letter-spacing: .03em; }
.nwmap-hdot { fill: #16457e; stroke: #fff; stroke-width: 1.2; }
.nwmap-tag.zoom { color: #0a1f3d; background: #ffb066; }
.nwmap-canvas { padding: 4px; }
/* 바다를 밝게 유지해 육지와의 명도차 확보 (저시력 대륙 윤곽 구분) */
.nwmap-canvas.world { background: linear-gradient(160deg, #eaf3fc, #cfe3f7); flex: 1; display: flex; align-items: center; justify-content: center; }
.nwmap-canvas.inset { background: radial-gradient(120% 120% at 50% 0%, #0d2950 0%, #0a1f3d 70%); flex: 1; }
.nwmap-canvas svg { width: 100%; height: auto; display: block; }

.nwmap-geo { fill: #9cc0e6; stroke: #5586c1; stroke-width: .7; }
.nwmap-geo.home { fill: #ff9558; stroke: #ec7434; stroke-width: 1; }
/* 호버 프리뷰: 기존 #ffd0a3은 육지와 명도차 1.2:1로 거의 안 보임 → 진한 주황 + 굵은 외곽선 */
.nwmap-geo.preview { fill: #ffcda4; stroke: #f09a55; stroke-width: 1.3; }
.nwmap-geo.codable { cursor: pointer; }
.nwmap-canvas.world svg { touch-action: none; }
.nwmap-ring { fill: rgba(255, 106, 26, .22); stroke: #ff6a1a; stroke-width: 1.4; }
.nwmap-dot { fill: #ff6a1a; stroke: #fff; stroke-width: 1.2; }
.nwmap-stem { stroke: #e85d12; stroke-width: 1.4; }
.nwmap-pill { fill: #1f3a6e; }
.nwmap-pill-txt { fill: #fff; font-size: 8.5px; font-weight: 500; letter-spacing: .03em; }

/* 인셋: 선택국(밝은 파랑+밝은 외곽선) vs 이웃국(어두운 남색) 명도차 확대 */
.nwmap-geo2 { fill: #16304f; stroke: #4a7fc0; stroke-width: .8; }
.nwmap-geo2.home { fill: #3d77bd; stroke: #8cc0f5; stroke-width: 1.2; }
.nwmap-mk-ring { fill: rgba(255, 176, 102, .18); stroke: #ffb066; stroke-width: 1.2; }
/* geo:null 국가용 마커 강조 — 은은한 글로우 + 바깥으로 퍼지는 펄스 */
.nwmap-mk-glow { fill: rgba(255, 149, 88, .14); stroke: #ff9558; stroke-width: 1.4; }
.nwmap-mk-pulse { fill: none; stroke: #ff9558; stroke-width: 2; transform-box: fill-box; transform-origin: center; animation: nwmapPulse 2.2s ease-out infinite; }
@keyframes nwmapPulse {
  0% { transform: scale(.5); opacity: .9; }
  100% { transform: scale(1.9); opacity: 0; }
}
.nwmap-mk { fill: #ffb066; stroke: #0a1f3d; stroke-width: 1; }
.nwmap-mk-g { cursor: pointer; }
/* 호버: 마커 테두리 + 라벨 강조 — 라벨↔마커 소속 관계 표시 (별도 링 없이 심플하게) */
.nwmap-mk-g:hover .nwmap-mk, .nwmap-mk-g:hover .nwmap-mk2 { stroke: #ffb066; stroke-width: 2; }
.nwmap-mk-g:hover .nwmap-mk-label { fill: #fff; font-weight: 700; text-decoration: underline; }
.nwmap-mk2 { fill: #8cc0f5; stroke: #0a1f3d; stroke-width: 1; }
/* viewBox(360) 좌표계가 실제 픽셀보다 커서 축소 렌더링됨 → 화면상 ~10px가 되도록 viewBox 단위로 크게 지정 */
.nwmap-mk-label { fill: #eaf2fd; font-size: 15px; font-weight: 600; paint-order: stroke; stroke: #0a1f3d; stroke-width: 3px; stroke-linejoin: round; }

@media (max-width: 840px) {
  .nwmap { flex-direction: column; }
  .nwmap-inset { flex-basis: auto; }
}

/* 모션 최소화 설정 사용자: 인셋 등장 애니메이션·마커 펄스 제거 (fly-to는 JS에서 즉시 이동 처리) */
@media (prefers-reduced-motion: reduce) {
  .nwmap-inset { animation: none; }
  .nwmap-mk-pulse { animation: none; opacity: 0; }
}
</style>
